import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "@/components/common/Widgets";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Cell, PieChart, Pie, Legend, LineChart, Line } from "recharts";
import { fmtNum, fmtDate } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, Bell, Loader2, AlertTriangle, ShieldAlert, Settings2, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const COLORS = ["#1F2A44", "#0F766E", "#D97706", "#DC2626", "#16A34A", "#9333EA", "#0EA5E9", "#475569", "#0891B2", "#A16207", "#7C3AED"];
const SB_COLORS = { SB1: "#16A34A", SB2: "#22C55E", SB3: "#65A30D", SB4: "#0EA5E9", SB5: "#0EA5E9", SB6: "#F59E0B", SB7: "#F97316", SB8: "#EF4444", SB9: "#DC2626", SB10: "#991B1B" };
const VERDICT_COLORS = { APPROVED: "#16A34A", REFER: "#D97706", REJECTED: "#DC2626" };

export default function MisPage() {
  return (
    <div className="space-y-3">
      <SectionHeader title="MIS Analytics" subtitle="Turn-around-time, deviation analytics, risk heatmap, branch performance, eligibility outcomes." />
      <Tabs defaultValue="eligibility">
        <TabsList className="bg-muted/60 flex-wrap h-auto">
          <TabsTrigger value="eligibility" data-testid="mis-tab-eligibility">Eligibility</TabsTrigger>
          <TabsTrigger value="tat" data-testid="mis-tab-tat">Turn-around Time</TabsTrigger>
          <TabsTrigger value="deviations" data-testid="mis-tab-deviations">Deviations</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="mis-tab-heatmap">Risk Heatmap</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="mis-tab-leaderboard">Branch Leaderboard</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="mis-tab-jobs">Auto Job Metrics</TabsTrigger>
        </TabsList>
        <TabsContent value="eligibility"><EligibilityTab /></TabsContent>
        <TabsContent value="tat"><TatTab /></TabsContent>
        <TabsContent value="deviations"><DeviationsTab /></TabsContent>
        <TabsContent value="heatmap"><HeatmapTab /></TabsContent>
        <TabsContent value="leaderboard"><LeaderboardTab /></TabsContent>
        <TabsContent value="jobs"><JobsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function EligibilityTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("90");
  const [loanType, setLoanType] = useState("all");
  const [alerts, setAlerts] = useState([]);
  const [alertRunning, setAlertRunning] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days };
      if (loanType !== "all") params.loan_type = loanType;
      const [r, ar] = await Promise.all([
        api.get("/mis/eligibility", { params }),
        api.get("/mis/foir-alerts", { params: { limit: 10 } }).catch(() => ({ data: [] })),
      ]);
      setData(r.data);
      setAlerts(ar.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [days, loanType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ days });
      if (loanType !== "all") params.set("loan_type", loanType);
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const token = localStorage.getItem("acas_token");
      const res = await fetch(`${backend}/api/mis/eligibility/export.csv?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eligibility-snapshots-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const runAlertCheck = async (dryRun = false) => {
    setAlertRunning(true);
    try {
      const r = await api.post("/mis/foir-alert-check", null, {
        params: { threshold_pct: 55, window_days: 30, min_sample: 3, dry_run: dryRun },
      });
      const fired = r.data?.alerts_fired || 0;
      const skipped = r.data?.skipped?.length || 0;
      if (dryRun) {
        toast.info(`Dry-run: ${fired} branch(es) would be alerted, ${skipped} skipped`);
      } else if (fired > 0) {
        toast.success(`${fired} branch alert(s) fired (${skipped} skipped)`);
      } else {
        toast.info(`No alerts fired — ${skipped} branch(es) checked but none breached`);
      }
      // Refresh the alerts panel
      const ar = await api.get("/mis/foir-alerts", { params: { limit: 10 } });
      setAlerts(ar.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Alert check failed");
    } finally {
      setAlertRunning(false);
    }
  };

  if (loading && !data) return <div className="mt-3 text-sm text-muted-foreground">Loading…</div>;
  if (!data || data.total_with_snapshot === 0) return (
    <div className="mt-3 space-y-3">
      <FilterBar
        days={days} setDays={setDays} loanType={loanType} setLoanType={setLoanType}
        onExport={exportCSV} exporting={exporting}
        onRunAlerts={() => runAlertCheck(false)} onDryRunAlerts={() => runAlertCheck(true)} alertRunning={alertRunning}
      />
      <Card className="p-12 text-center border-dashed">
        <div className="text-sm font-semibold heading">No eligibility snapshots in this window</div>
        <div className="text-xs text-muted-foreground mt-1">Snapshots are computed automatically when a retail (Personal/Housing) loan application is created with income data, or on lead conversion. Try widening the time window.</div>
      </Card>
    </div>
  );

  // Stack-bar data for branch & loan type
  const branchStack = data.by_branch.map((b) => ({
    branch: b.branch,
    APPROVED: b.APPROVED,
    REFER: b.REFER,
    REJECTED: b.REJECTED,
  }));
  const ltStack = data.by_loan_type.map((b) => ({
    loan_type: b.loan_type.replace("_", " "),
    APPROVED: b.APPROVED,
    REFER: b.REFER,
    REJECTED: b.REJECTED,
  }));
  const verdictPie = data.by_verdict.filter((v) => v.count > 0);

  return (
    <div className="mt-3 space-y-3" data-testid="mis-eligibility-content">
      <FilterBar
        days={days} setDays={setDays} loanType={loanType} setLoanType={setLoanType}
        onExport={exportCSV} exporting={exporting}
        onRunAlerts={() => runAlertCheck(false)} onDryRunAlerts={() => runAlertCheck(true)} alertRunning={alertRunning}
      />

      {/* Headline KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard k="Snapshots" v={data.total_with_snapshot} testid="mis-elig-kpi-total" />
        <KpiCard k="Eligible Rate" v={`${data.eligible_rate_pct}%`} tone={data.eligible_rate_pct >= 60 ? "success" : data.eligible_rate_pct >= 30 ? "warning" : "destructive"} testid="mis-elig-kpi-rate" />
        <KpiCard k="Avg FOIR" v={`${data.avg_foir_pct}%`} tone={data.avg_foir_pct <= 40 ? "success" : data.avg_foir_pct <= 50 ? "warning" : "destructive"} testid="mis-elig-kpi-foir" />
        <KpiCard k="Avg Max Eligible" v={`₹${fmtNum(data.avg_max_eligible_lakhs)}L`} testid="mis-elig-kpi-maxamt" />
      </div>

      {/* Verdict donut + FOIR histogram */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-2">Verdict Distribution</div>
          <div className="h-[260px]" data-testid="mis-elig-verdict-donut">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={verdictPie} dataKey="count" nameKey="verdict" innerRadius={60} outerRadius={95} paddingAngle={3} label={({ verdict, count }) => `${verdict}: ${count}`}>
                  {verdictPie.map((v) => <Cell key={v.verdict} fill={VERDICT_COLORS[v.verdict]} />)}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-2">FOIR Distribution</div>
          <div className="text-xs text-muted-foreground mb-2">How borrowers cluster on the Fixed-Obligation-to-Income axis. Policy cap is typically 50%.</div>
          <div className="h-[230px]" data-testid="mis-elig-foir-histogram">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.foir_distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="bucket" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4,4,0,0]}>
                  {data.foir_distribution.map((b, i) => {
                    const lo = parseInt(b.bucket.split("-")[0] || b.bucket, 10);
                    const color = lo >= 60 ? "#DC2626" : lo >= 50 ? "#F97316" : lo >= 40 ? "#D97706" : "#16A34A";
                    return <Cell key={i} fill={color} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Branch and loan type stacked bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-2">By Branch</div>
          <div className="h-[260px]" data-testid="mis-elig-branch-stack">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={branchStack}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="branch" fontSize={11} angle={-15} textAnchor="end" height={50} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend iconType="circle" />
                <Bar dataKey="APPROVED" stackId="v" fill={VERDICT_COLORS.APPROVED} radius={[0,0,0,0]} />
                <Bar dataKey="REFER" stackId="v" fill={VERDICT_COLORS.REFER} radius={[0,0,0,0]} />
                <Bar dataKey="REJECTED" stackId="v" fill={VERDICT_COLORS.REJECTED} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-2">By Loan Type</div>
          <div className="h-[260px]" data-testid="mis-elig-loan-type-stack">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ltStack}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="loan_type" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Legend iconType="circle" />
                <Bar dataKey="APPROVED" stackId="v" fill={VERDICT_COLORS.APPROVED} />
                <Bar dataKey="REFER" stackId="v" fill={VERDICT_COLORS.REFER} />
                <Bar dataKey="REJECTED" stackId="v" fill={VERDICT_COLORS.REJECTED} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Time series */}
      <Card className="p-4">
        <div className="text-sm font-semibold heading mb-2">Daily Eligibility Outcomes (last {days} days)</div>
        <div className="h-[260px]" data-testid="mis-elig-time-series">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.time_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="day" fontSize={10} tickFormatter={(d) => d.slice(5)} interval={Math.max(0, Math.floor(data.time_series.length / 10))} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Legend iconType="line" />
              <Line type="monotone" dataKey="APPROVED" stroke={VERDICT_COLORS.APPROVED} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="REFER" stroke={VERDICT_COLORS.REFER} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="REJECTED" stroke={VERDICT_COLORS.REJECTED} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Top rejection reasons */}
      <Card className="p-4">
        <div className="text-sm font-semibold heading mb-2">Top Rejection Reasons</div>
        <div className="text-xs text-muted-foreground mb-2">Which policy checks are blocking the most retail loans.</div>
        {data.top_rejection_reasons.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">No rejected snapshots in this window — nice work!</div>
        ) : (
          <table className="w-full text-sm" data-testid="mis-elig-reject-reasons">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Check</th><th className="text-right px-3 py-2">Rejections</th></tr>
            </thead>
            <tbody>
              {data.top_rejection_reasons.map((r) => (
                <tr key={r.check} className="border-t border-border">
                  <td className="px-3 py-2 mono text-xs">{r.check}</td>
                  <td className="px-3 py-2 num-right">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* FOIR Alert Policies */}
      <FoirPoliciesPanel />

      {/* Recent FOIR breach alerts */}
      <Card className="p-4" data-testid="mis-elig-alerts-panel">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <div className="text-sm font-semibold heading">Recent FOIR Breach Alerts</div>
        </div>
        <div className="text-xs text-muted-foreground mb-2">
          Alerts fire when any branch's avg FOIR exceeds <span className="mono font-semibold">55%</span> over a 30-day window with at least 3 snapshots. Recipients: Branch Manager + HO. De-duped to once per branch per 24h.
        </div>
        {alerts.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">No alerts in history — run the check from the top-right button.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Branch</th>
                <th className="text-right px-3 py-2">Avg FOIR</th>
                <th className="text-right px-3 py-2">Threshold</th>
                <th className="text-right px-3 py-2">Samples</th>
                <th className="text-right px-3 py-2">Eligible %</th>
                <th className="text-right px-3 py-2">Recipients</th>
                <th className="text-left px-3 py-2">Fired At</th>
                <th className="text-left px-3 py-2">By</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((al) => (
                <tr key={al.id} className="border-t border-border" data-testid={`mis-elig-alert-${al.id}`}>
                  <td className="px-3 py-2 font-medium">{al.branch}</td>
                  <td className="px-3 py-2 num-right text-destructive font-semibold">{al.avg_foir_pct}%</td>
                  <td className="px-3 py-2 num-right">{al.threshold_pct}%</td>
                  <td className="px-3 py-2 num-right mono">{al.sample_size}</td>
                  <td className="px-3 py-2 num-right">{al.eligible_rate}%</td>
                  <td className="px-3 py-2 num-right mono">{al.recipients_count}</td>
                  <td className="px-3 py-2 text-xs mono text-muted-foreground">{fmtDate(al.fired_at)}</td>
                  <td className="px-3 py-2 text-xs">{al.fired_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function FilterBar({ days, setDays, loanType, setLoanType, onExport, exporting, onRunAlerts, onDryRunAlerts, alertRunning }) {
  return (
    <Card className="p-3 flex flex-wrap gap-2 items-center">
      <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Window</span>
      <Select value={days} onValueChange={setDays}>
        <SelectTrigger className="w-32" data-testid="mis-elig-days-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="7">Last 7 days</SelectItem>
          <SelectItem value="30">Last 30 days</SelectItem>
          <SelectItem value="90">Last 90 days</SelectItem>
          <SelectItem value="180">Last 6 months</SelectItem>
          <SelectItem value="365">Last 1 year</SelectItem>
        </SelectContent>
      </Select>
      <span className="text-xs uppercase tracking-wide text-muted-foreground font-medium ml-2">Loan Type</span>
      <Select value={loanType} onValueChange={setLoanType}>
        <SelectTrigger className="w-44" data-testid="mis-elig-loan-type-filter"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All retail loans</SelectItem>
          <SelectItem value="PERSONAL_LOAN">Personal Loan</SelectItem>
          <SelectItem value="HOUSING_LOAN">Housing Loan</SelectItem>
        </SelectContent>
      </Select>
      <div className="ml-auto flex gap-2">
        {onDryRunAlerts && (
          <Button variant="outline" size="sm" onClick={onDryRunAlerts} disabled={alertRunning} data-testid="mis-elig-dry-run-button">
            <ShieldAlert className="h-3.5 w-3.5 mr-1" />Dry-run Alerts
          </Button>
        )}
        {onRunAlerts && (
          <Button variant="outline" size="sm" onClick={onRunAlerts} disabled={alertRunning} data-testid="mis-elig-run-alerts-button">
            {alertRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Bell className="h-3.5 w-3.5 mr-1" />}
            {alertRunning ? "Checking…" : "Run FOIR Alerts"}
          </Button>
        )}
        {onExport && (
          <Button size="sm" onClick={onExport} disabled={exporting} data-testid="mis-elig-export-csv-button">
            {exporting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
            Export CSV
          </Button>
        )}
      </div>
    </Card>
  );
}

function KpiCard({ k, v, tone, testid }) {
  const toneClasses = {
    success: "border-success/30 bg-success/5",
    warning: "border-warning/30 bg-warning/5",
    destructive: "border-destructive/30 bg-destructive/5",
  };
  return (
    <Card className={`p-3 ${tone ? toneClasses[tone] : ""}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">{k}</div>
      <div className="mt-1 text-2xl mono font-semibold tabular" data-testid={testid}>{v}</div>
    </Card>
  );
}


function TatTab() {
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/mis/tat").then(r => setData(r.data)); }, []);
  return (
    <Card className="p-4 mt-3">
      <div className="text-sm font-semibold heading mb-2">Average time spent per stage (hours)</div>
      {data.length === 0 ? <EmptyState text="Not enough stage transitions yet to compute TAT" /> : (
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="stage" angle={-25} textAnchor="end" interval={0} fontSize={11} height={80} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="avg_hours" fill="hsl(186 55% 28%)" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function DeviationsTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/mis/deviations").then(r => setD(r.data)); }, []);
  if (!d) return <div className="mt-3 text-sm text-muted-foreground">Loading…</div>;
  return (
    <div className="mt-3 space-y-3">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-2">By Severity</div>
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(d.by_severity).map(([k, v]) => (
              <div key={k} className={`rounded-md border p-3 ${k === "HARD_STOP" ? "border-destructive/30 bg-destructive/5" : k === "DEVIATION" ? "border-warning/30 bg-warning/5" : "border-info/30 bg-info/5"}`}>
                <div className="text-[11px] uppercase font-medium">{k}</div>
                <div className="text-2xl font-semibold mono mt-1">{v}</div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-sm font-semibold heading mb-2">By Loan Type</div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.by_loan_type}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="loan_type" fontSize={10} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(38 92% 50%)" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
      <Card className="p-4">
        <div className="text-sm font-semibold heading mb-2">Top Failing Rules</div>
        {d.by_rule.length === 0 ? <EmptyState text="No deviations recorded yet" /> : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2">Rule</th><th className="text-right px-3 py-2">Count</th></tr></thead>
            <tbody>{d.by_rule.map((r, i) => <tr key={i} className="border-t border-border"><td className="px-3 py-2">{r.rule}</td><td className="px-3 py-2 num-right">{r.count}</td></tr>)}</tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function HeatmapTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/mis/risk-heatmap").then(r => setD(r.data)); }, []);
  if (!d) return <div className="mt-3 text-sm text-muted-foreground">Loading…</div>;
  const branches = [...new Set(d.branch_x_sb.map(r => r.branch))];
  const sbs = ["SB1", "SB2", "SB3", "SB4", "SB5", "SB6", "SB7", "SB8", "SB9", "SB10"];
  const cellAt = (b, s) => d.branch_x_sb.find(r => r.branch === b && r.sb === s)?.count || 0;
  const max = Math.max(1, ...d.branch_x_sb.map(r => r.count));
  return (
    <div className="mt-3 space-y-3">
      <Card className="p-4">
        <div className="text-sm font-semibold heading mb-3">Branch × SB Rating Heatmap</div>
        {branches.length === 0 ? <EmptyState text="Run CRA on a few applications to populate the heatmap" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr><th className="text-left px-3 py-2">Branch \ SB</th>{sbs.map(s => <th key={s} className="px-3 py-2 mono text-xs" style={{ backgroundColor: SB_COLORS[s] + "22" }}>{s}</th>)}</tr></thead>
              <tbody>
                {branches.map(b => (
                  <tr key={b} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{b}</td>
                    {sbs.map(s => {
                      const v = cellAt(b, s);
                      const intensity = max ? (v / max) : 0;
                      return <td key={s} className="px-3 py-2 num-right mono" style={{ backgroundColor: v ? `${SB_COLORS[s]}${Math.round(intensity * 90 + 10).toString(16).padStart(2,'0')}` : undefined, color: intensity > 0.5 ? "white" : undefined }}>{v || ""}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function LeaderboardTab() {
  const [d, setD] = useState([]);
  useEffect(() => { api.get("/mis/branch-leaderboard").then(r => setD(r.data)); }, []);
  return (
    <Card className="p-0 overflow-hidden mt-3">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">Branch</th><th className="text-right px-3 py-2.5">Total</th><th className="text-right px-3 py-2.5">Sanctioned</th><th className="text-right px-3 py-2.5">Rejected</th><th className="text-right px-3 py-2.5">In Progress</th><th className="text-right px-3 py-2.5">Sanction %</th><th className="text-right px-3 py-2.5">Total Facility (₹ L)</th></tr></thead>
        <tbody>
          {d.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">No data yet.</td></tr>}
          {d.map(r => (
            <tr key={r.branch} className="border-t border-border"><td className="px-3 py-2 font-medium">{r.branch}</td><td className="px-3 py-2 num-right">{r.total}</td><td className="px-3 py-2 num-right text-success">{r.sanctioned}</td><td className="px-3 py-2 num-right text-destructive">{r.rejected}</td><td className="px-3 py-2 num-right">{r.in_progress}</td><td className="px-3 py-2 num-right">{r.sanction_rate}%</td><td className="px-3 py-2 num-right">{fmtNum(r.total_facility_lakhs)}</td></tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function JobsTab() {
  const [d, setD] = useState([]);
  const [jobs, setJobs] = useState([]);
  useEffect(() => { api.get("/mis/job-metrics").then(r => setD(r.data)); api.get("/jobs?limit=20").then(r => setJobs(r.data)); }, []);
  return (
    <div className="space-y-3 mt-3">
      <Card className="p-4">
        <div className="text-sm font-semibold heading mb-3">Auto Job Metrics</div>
        {d.length === 0 ? <EmptyState text="No AI jobs run yet" /> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {d.map(m => (
              <Card key={m.type} className="p-3 border-border">
                <div className="text-xs uppercase font-medium text-muted-foreground">{m.type}</div>
                <div className="mt-1 flex items-baseline gap-2"><div className="text-2xl font-semibold mono">{m.success_rate}%</div><Badge variant="outline" className="text-[10px]">success</Badge></div>
                <div className="mt-1 text-xs text-muted-foreground">{m.total} runs · avg {(m.avg_duration_ms / 1000).toFixed(1)}s</div>
                <div className="mt-1 text-xs"><span className="text-success">{m.success} ok</span> · <span className="text-destructive">{m.failed} failed</span> · <span className="text-warning">{m.retrying} retrying</span></div>
              </Card>
            ))}
          </div>
        )}
      </Card>
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30 text-sm font-semibold">Recent Jobs</div>
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="text-left px-3 py-2.5">Job ID</th><th className="text-left px-3 py-2.5">Type</th><th className="text-left px-3 py-2.5">Status</th><th className="text-right px-3 py-2.5">Attempts</th><th className="text-right px-3 py-2.5">Duration</th><th className="text-left px-3 py-2.5">Correlation</th></tr></thead>
          <tbody>
            {jobs.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No jobs yet.</td></tr>}
            {jobs.map(j => (
              <tr key={j.id} className="border-t border-border"><td className="px-3 py-2 mono text-[11px]">{j.id.slice(0, 14)}…</td><td className="px-3 py-2 text-xs">{j.type}</td><td className="px-3 py-2"><Badge variant="outline" className={j.status === "SUCCESS" ? "text-success border-success/40" : j.status === "FAILED" ? "text-destructive border-destructive/40" : "text-warning border-warning/40"}>{j.status}</Badge></td><td className="px-3 py-2 num-right">{j.attempts}</td><td className="px-3 py-2 num-right mono text-xs">{j.duration_ms ? `${(j.duration_ms/1000).toFixed(1)}s` : "—"}</td><td className="px-3 py-2 mono text-[10px] text-muted-foreground">{(j.correlation_id || "").slice(0, 12)}…</td></tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="py-10 text-center text-sm text-muted-foreground">{text}</div>;
}


function FoirPoliciesPanel() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/policy/foir-alerts");
      setPolicies(r.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const deletePolicy = async (id) => {
    if (!window.confirm("Delete this FOIR alert policy? Branches will fall back to the global default after this.")) return;
    try {
      await api.delete(`/policy/foir-alerts/${id}`);
      toast.success("Policy deleted");
      fetchPolicies();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="p-4" data-testid="mis-elig-policies-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-accent" />
          <div className="text-sm font-semibold heading">FOIR Alert Policies</div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setEditing({ loan_type: "ALL", threshold_pct: 55, window_days: 30, min_sample: 3, enabled: true, notes: "" })} data-testid="mis-elig-add-policy-button">
          <Plus className="h-3.5 w-3.5 mr-1" />New Policy
        </Button>
      </div>
      <div className="text-xs text-muted-foreground mb-3">
        Configure per-loan-type FOIR thresholds. Lookup order: <span className="mono font-semibold">bank+loan_type → bank+ALL → global+loan_type → global+ALL → fallback 55%/30d/3</span>.
        Used by the daily cron and the manual <span className="font-semibold">Run FOIR Alerts</span> button.
      </div>
      {loading ? (
        <div className="text-sm text-muted-foreground py-4">Loading…</div>
      ) : policies.length === 0 ? (
        <div className="text-sm text-muted-foreground py-3 border border-dashed rounded-md text-center">No custom policies — all branches use the fallback default (55% FOIR, 30d, 3 samples).</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2">Scope</th>
              <th className="text-left px-3 py-2">Loan Type</th>
              <th className="text-right px-3 py-2">Threshold</th>
              <th className="text-right px-3 py-2">Window</th>
              <th className="text-right px-3 py-2">Min Samples</th>
              <th className="text-center px-3 py-2">Enabled</th>
              <th className="text-left px-3 py-2">Notes</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid={`mis-elig-policy-${p.id}`}>
                <td className="px-3 py-2 text-xs"><Badge variant="outline" className="text-[10px]">{p.bank_id ? "Bank" : "Global"}</Badge></td>
                <td className="px-3 py-2 mono text-xs">{p.loan_type}</td>
                <td className="px-3 py-2 num-right font-semibold tabular">{p.threshold_pct}%</td>
                <td className="px-3 py-2 num-right">{p.window_days}d</td>
                <td className="px-3 py-2 num-right">{p.min_sample}</td>
                <td className="px-3 py-2 text-center">{p.enabled ? <Badge className="bg-success/15 text-success border-success/40 text-[10px]">ON</Badge> : <Badge className="bg-muted text-muted-foreground text-[10px]">OFF</Badge>}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[240px]">{p.notes || "—"}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => setEditing({ ...p })} data-testid={`mis-elig-policy-edit-${p.id}`}><Settings2 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePolicy(p.id)} data-testid={`mis-elig-policy-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <FoirPolicyDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchPolicies(); }}
        />
      )}
    </Card>
  );
}

function FoirPolicyDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    loan_type: initial.loan_type || "ALL",
    threshold_pct: initial.threshold_pct ?? 55,
    window_days: initial.window_days ?? 30,
    min_sample: initial.min_sample ?? 3,
    enabled: initial.enabled !== false,
    notes: initial.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/policy/foir-alerts", {
        loan_type: form.loan_type,
        threshold_pct: Number(form.threshold_pct),
        window_days: Number(form.window_days),
        min_sample: Number(form.min_sample),
        enabled: form.enabled,
        notes: form.notes || null,
      });
      toast.success("Policy saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mis-elig-policy-dialog">
        <DialogHeader>
          <DialogTitle>{initial.id ? "Edit" : "New"} FOIR Alert Policy</DialogTitle>
          <DialogDescription>Sets the threshold above which an automatic alert fires for the chosen loan type within your bank.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5 col-span-2">
            <Label>Loan Type *</Label>
            <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)} disabled={!!initial.id}>
              <SelectTrigger data-testid="mis-elig-policy-loan-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All retail loans (bank-wide default)</SelectItem>
                <SelectItem value="PERSONAL_LOAN">Personal Loan</SelectItem>
                <SelectItem value="HOUSING_LOAN">Housing Loan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>FOIR Threshold % *</Label><Input type="number" step="0.5" value={form.threshold_pct} onChange={(e) => set("threshold_pct", e.target.value)} data-testid="mis-elig-policy-threshold" /></div>
          <div className="space-y-1.5"><Label>Window (days) *</Label><Input type="number" value={form.window_days} onChange={(e) => set("window_days", e.target.value)} data-testid="mis-elig-policy-window" /></div>
          <div className="space-y-1.5"><Label>Min Samples *</Label><Input type="number" value={form.min_sample} onChange={(e) => set("min_sample", e.target.value)} data-testid="mis-elig-policy-min-sample" /></div>
          <div className="space-y-1.5 flex flex-col">
            <Label>Enabled</Label>
            <div className="pt-2"><Switch checked={form.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="mis-elig-policy-enabled" /></div>
          </div>
          <div className="space-y-1.5 col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Why this threshold? (e.g. 'Q2 review tightened from 55% → 48%')" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="mis-elig-policy-save">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save Policy</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
